/**
 * SubviewHost: the shell of the Team and Manage tabs (plan section 13 tabs 6
 * and 7): the tab's PageHeader, a SegmentedTabs bar over the tab's sub-view
 * registry, and the active sub-view below it.
 *
 * The active sub-view is addressed by URL (/lms/<tab>/<subview path>), so a
 * deep link, an email link or a reload lands on the right view; no segment or
 * an unknown one redirects to the first sub-view. The sub-view is mounted
 * under a nested route "<path>/*", so any <Routes> it declares itself resolve
 * under /lms/<tab>/<path>/ (for example People's /lms/team/people/<userId>).
 *
 * Hot file (plan 15.12): WP-03 only.
 */
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../ui/PageHeader';
import SegmentedTabs from '../../ui/SegmentedTabs';
import { lmsPageCls } from './layout/page';
import { lmsTabHref } from '../tabs/config';
import type { LmsSubview, LmsTabConfig } from '../tabs/config';

export interface SubviewHostProps {
  tab: LmsTabConfig;
  subviews: LmsSubview[];
}

export function SubviewHost({ tab, subviews }: SubviewHostProps) {
  const params = useParams();
  const navigate = useNavigate();
  if (subviews.length === 0) throw new Error(`The ${tab.title} tab has no sub-views registered`);

  const base = lmsTabHref(tab);
  const segment = (params['*'] ?? '').split('/')[0];
  const active = subviews.find((s) => s.path === segment);
  if (!active) return <Navigate to={`${base}/${subviews[0].path}`} replace />;

  const Active = active.Component;
  return (
    <div className={lmsPageCls}>
      <PageHeader title={tab.title} icon={tab.icon} subtitle={active.description} />
      <SegmentedTabs
        label={`${tab.title} views`}
        tabs={subviews.map((s) => ({ value: s.path, label: s.label }))}
        value={active.path}
        onChange={(value) => navigate(`${base}/${value}`)}
      />
      <Routes>
        <Route path={`${active.path}/*`} element={<Active />} />
      </Routes>
    </div>
  );
}

export default SubviewHost;
